import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/mahjong';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'guobiao');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const relatedArticles = articles.filter((a) => a.category === activeSlug);

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory.name} - 在线麻将牌`,
          description: currentCategory.description,
          url: `https://tileblast.github.io/category/${activeSlug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '在线麻将牌',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tileblast.github.io' },
            { '@type': 'ListItem', position: 2, name: '玩法分类', item: 'https://tileblast.github.io/category/guobiao' },
            { '@type': 'ListItem', position: 3, name: currentCategory.name },
          ],
        }}
      />

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '玩法分类', path: '/category/guobiao' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={currentCategory.image}
            alt={`${currentCategory.name} - 在线麻将牌`}
            className="absolute inset-0 w-full h-full object-cover object-top"
            title={`${currentCategory.name} - 在线麻将牌`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm text-white mb-4">
              <i className={`${currentCategory.icon} text-2xl`}></i>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
              {currentCategory.name}
            </h1>
            <p className="text-white/80 max-w-xl text-sm md:text-base">
              {currentCategory.description}
            </p>
            <div className="flex items-center gap-3 mt-4">
              <span className="bg-accent-500 text-background-50 text-sm px-3 py-1 rounded-full whitespace-nowrap">
                {currentCategory.difficulty}
              </span>
              <span className="bg-white/15 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full whitespace-nowrap">
                {currentCategory.playerCount}
              </span>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main */}
              <div className="lg:col-span-2">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  {currentCategory.name} · 相关攻略
                </h2>
                {relatedArticles.length > 0 ? (
                  <div className="space-y-4">
                    {relatedArticles.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        className="flex flex-col sm:flex-row gap-4 bg-background-50 border border-background-200/70 rounded-xl p-4 hover:border-primary-300 transition-all cursor-pointer group"
                      >
                        <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            title={article.title}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-foreground-500 whitespace-nowrap">
                              {article.date}
                            </span>
                            <span className="text-xs text-foreground-400">·</span>
                            <span className="text-xs text-foreground-500 whitespace-nowrap">
                              {article.readTime}
                            </span>
                          </div>
                          <h3 className="font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                            {article.excerpt}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-foreground-500">
                    <p>暂无该分类的相关文章，敬请期待！</p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                <div className="bg-background-100 rounded-xl p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-foreground-900 mb-4">
                    玩法信息
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-500">难度等级</span>
                      <span className="font-medium text-foreground-900">{currentCategory.difficulty}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">游戏人数</span>
                      <span className="font-medium text-foreground-900">{currentCategory.playerCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">相关文章</span>
                      <span className="font-medium text-foreground-900">{relatedArticles.length} 篇</span>
                    </div>
                  </div>
                  <div className="mt-5 pt-5 border-t border-background-200">
                    <h4 className="font-heading font-semibold text-foreground-900 mb-3">
                      其他玩法
                    </h4>
                    <div className="space-y-2">
                      {categories
                        .filter((c) => c.slug !== activeSlug)
                        .slice(0, 4)
                        .map((cat) => (
                          <Link
                            key={cat.slug}
                            to={`/category/${cat.slug}`}
                            className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors py-1 cursor-pointer"
                          >
                            <span className="w-5 h-5 flex items-center justify-center">
                              <i className={`${cat.icon} text-xs`}></i>
                            </span>
                            {cat.name}
                          </Link>
                        ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-primary-600">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
              马上体验{currentCategory.name}
            </h2>
            <p className="text-primary-100 mb-8">
              选择你喜欢的房间，秒速匹配对手，开始你的{currentCategory.name}之旅！
            </p>
            <button className="px-8 py-3 bg-accent-500 text-background-50 rounded-full text-base font-semibold hover:bg-accent-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
              开始游戏
            </button>
          </div>
        </section>
      </div>
    </>
  );
}